/**
 * Families of API error codes that read the same to a person. `errorText`
 * uses them when a code has no exact `code_<code>` translation, so a new
 * `foo_not_found` on the server is never shown as raw English ("Request
 * failed") in a Russian UI. First match wins — order matters.
 *
 * `src/__arch__/api-error-codes.spec.ts` fails when the API declares a code
 * that is neither translated, nor in a family, nor allowlisted as internal.
 */
export const ERROR_GROUPS = [
  [
    'group_session',
    /^(unauthenticated|unauthorized|missing_bearer|refresh_token_reuse|token_wrong_type|stream_revoked)$|^session_|_session$|^pat_(inactive|revoked|parent_revoked)$/,
  ],
  ['group_concurrent', /_race$|_retry$|_conflict$|concurrent|_changed$|_mismatch$|version|in_progress$|_reused$/],
  ['group_not_found', /(^|_)not_found|_missing$|^unknown_|_unknown$|_unknown_|_gone$|_deleted$|_unreachable$|_not_in_/],
  [
    'group_forbidden',
    /forbidden|denied|not_allowed|not_permitted|^not_|_not_(a_)?(team_)?member|not_owner|not_requester|not_your|not_author|_only$|^only_|cannot_|cant_|not_authori[sz]ed|no_access|insufficient|escalation|^role_|requires_2fa|requires_mfa|_mfa_|not_yours|not_owned|disallowed|restricted|not_eligible|sod_violation|not_superadmin|_scope_reserved$|_team_scope$|missing_scope|requires_team_edit|^team_overview_contractor$/,
  ],
  [
    'group_inactive',
    /banned|inactive|_not_active$|not_verified$|unverified|^plan_inactive$|_declined$|not_discoverable$/,
  ],
  ['group_exists', /_taken$|_exists$|already_|duplicate|_in_use$|collision|_unchanged$/],
  ['group_expired', /expired|_stale$|too_old|in_past$/],
  [
    'group_limit',
    /too_(long|large|many|big|short|wide|deep)|limit|exceeded|quota|^max_|_cap$|over_budget|cooldown|at_ceiling$|at_floor$|^FREE_TIER_/,
  ],
  [
    'group_locked',
    /_locked|_archived$|_closed$|_frozen$|read_only|readonly|has_pending|has_hidden|retired$|ready_to_complete$|_not_pending$|no_op$|not_running$|after_invoicing$/,
  ],
  ['group_workflow', /^workflow_|circular|cycle|dead_end|self_loop|^policy_/],
  [
    'group_unavailable',
    /unavailable|disabled|not_configured|unconfigured|not_enabled|not_installed|misconfigured|not_implemented|not_wired|coming_soon|_down$|_failed$|^failed_|timeout|upstream|_error$|unreachable|not_purchased|no_checkout|no_portal|integration_inactive$/,
  ],
  [
    'group_invalid',
    /invalid|malformed|bad_|unsupported|_format|must_|required|empty|unprocessable|not_supported|nothing_to_update|extra_fields|wrong_|deprecated|in_future$|partial|incomplete|needs_|no_pending|not_personal|has_no|non_|multiple|ordinals|_to_self$|_self$|^custom$|_in_past$|no_escalation|_requires_|^requires_approval$|only_field$/,
  ],
] as const satisfies ReadonlyArray<readonly [string, RegExp]>;

export type ErrorGroupKey = (typeof ERROR_GROUPS)[number][0];

/** The family an API error code belongs to, or null when none fits. */
export function errorGroupOf(code: string): ErrorGroupKey | null {
  for (const [key, pattern] of ERROR_GROUPS) if (pattern.test(code)) return key;
  return null;
}
